/* ==========================================================================
   blv-countdown.js — the 72-hour countdown
   ========================================================================== */

(function () {
  'use strict';

  var instances = new WeakMap();

  var SECOND = 1000;
  var MINUTE = 60;
  var HOUR = 3600;
  var DAY = 86400;

  var UNITS = ['days', 'hours', 'minutes', 'seconds'];

  function forEach(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function toInt(value, fallback) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* ---------- Server clock ---------- */
  function serverOffset(root) {
    var serverNow = toInt(root.dataset.serverNow, 0);
    if (!serverNow) return 0;

    var delta = serverNow * SECOND - Date.now();
    return Math.abs(delta) < 2 * SECOND ? 0 : delta;
  }

  /* ---------- Evergreen: a private deadline per visitor ---------- */
  function readStored(key) {
    try {
      return toInt(window.localStorage.getItem(key), 0);
    } catch (error) {
      return 0;
    }
  }

  function writeStored(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (error) {
      /* Storage unavailable — the visitor simply gets a fresh window each
         visit, which is a better outcome than an exception. */
    }
  }

  function evergreenDeadline(key, durationMs, nowMs) {
    var stored = readStored(key);

    if (!stored || stored <= nowMs) {
      stored = nowMs + durationMs;
      writeStored(key, stored);
    }

    return stored;
  }

  /* ---------- Rolling window: the same deadline for everyone, on repeat ---------- */
  function rollingDeadline(anchorMs, durationMs, nowMs) {
    if (durationMs <= 0) return anchorMs;
    if (anchorMs > nowMs) return anchorMs;

    var elapsed = nowMs - anchorMs;
    return anchorMs + (Math.floor(elapsed / durationMs) + 1) * durationMs;
  }

  /* ---------- Split remaining seconds across the enabled units ---------- */
  function split(totalSeconds, enabled) {
    var remaining = Math.max(0, totalSeconds);
    var parts = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    var sizes = { days: DAY, hours: HOUR, minutes: MINUTE, seconds: 1 };
    var anyEnabled = false;

    UNITS.forEach(function (unit) {
      if (!enabled[unit]) return;

      parts[unit] = Math.floor(remaining / sizes[unit]);
      remaining -= parts[unit] * sizes[unit];
      anyEnabled = true;
    });

    /* Nothing enabled at all: fall back to seconds so the section still says
       something rather than rendering an empty row. */
    if (!anyEnabled) parts.seconds = remaining;

    return parts;
  }

  /* ---------- Sticky offset: how far down the header actually ends ---------- */
  var STICKY_VAR = '--blv-cd-sticky-top';

  var HEIGHT_VAR = '--blv-cd-bar-height';

  function measureHeaderOffset() {
    var bar = document.querySelector('[data-blv-countdown-sticky]');

    if (!bar) return;

    var group = document.querySelector('[data-blv-header-group]');

    /* No header on the page at all — leave the property unset so the CSS
       fallback stays in charge rather than pinning the bar to 0. */
    if (!group) return;

    document.documentElement.style.setProperty(STICKY_VAR, group.offsetHeight + 'px');

    document.documentElement.style.setProperty(HEIGHT_VAR, bar.offsetHeight + 'px');
  }

  var resizeFrame = null;

  function onResize() {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(function () {
      resizeFrame = null;
      measureHeaderOffset();
    });
  }

  function init(root) {
    var scope = root || document;

    measureHeaderOffset();

    forEach(scope.querySelectorAll('[data-blv-countdown]'), function (el) {
      if (instances.has(el)) return;

      var section = el.closest('[data-blv-countdown-section]') || el;
      var timer = el.querySelector('[data-blv-countdown-timer]');
      var expired = el.querySelector('[data-blv-countdown-expired]');
      var status = el.querySelector('[data-blv-countdown-status]');

      var mode = el.dataset.mode || 'fixed_date';
      var behavior = el.dataset.expiry || 'message';
      var redirect = el.dataset.redirect || '';
      var storageKey = el.dataset.storageKey || 'blv-countdown';
      var durationMs = toInt(el.dataset.duration, 0) * SECOND;
      var endMs = toInt(el.dataset.end, 0) * SECOND;
      var offset = serverOffset(el);

      var enabled = {};
      var nodes = {};
      UNITS.forEach(function (unit) {
        nodes[unit] = el.querySelector('[data-blv-cd-unit="' + unit + '"]');
        enabled[unit] = !!nodes[unit];
      });

      var labels = {};
      UNITS.forEach(function (unit) {
        var node = el.querySelector('[data-blv-cd-label="' + unit + '"]');
        labels[unit] = node ? node.textContent.trim() : unit;
      });

      var teardown = [];
      var interval = null;
      var finished = false;

      function now() {
        return Date.now() + offset;
      }

      function resolveDeadline() {
        if (mode === 'evergreen') return evergreenDeadline(storageKey, durationMs, now());
        if (mode === 'rolling_window') return rollingDeadline(endMs, durationMs, now());
        return endMs;
      }

      var deadline = resolveDeadline();

      function stop() {
        if (interval === null) return;
        window.clearInterval(interval);
        interval = null;
      }

      /* Only touch the DOM when the rendered text actually differs. */
      function write(node, text) {
        if (node && node.textContent !== text) node.textContent = text;
      }

      function paint(totalSeconds) {
        var parts = split(totalSeconds, enabled);
        var spoken = [];

        UNITS.forEach(function (unit) {
          if (!nodes[unit]) return;
          write(nodes[unit], pad(parts[unit]));
          spoken.push(parts[unit] + ' ' + labels[unit]);
        });

        write(status, spoken.join(', ') + ' remaining');
      }

      function expire() {
        if (finished) return;
        finished = true;

        if (behavior === 'restart' && durationMs > 0) {
          finished = false;
          if (mode === 'evergreen') {
            deadline = now() + durationMs;
            writeStored(storageKey, deadline);
          } else {
            deadline = rollingDeadline(deadline, durationMs, now());
          }
          tick();
          return;
        }

        stop();
        paint(0);

        if (behavior === 'hide') {
          section.hidden = true;
          return;
        }

        if (behavior === 'redirect' && redirect) {
          window.location.href = redirect;
          return;
        }

        if (expired) {
          if (timer) timer.hidden = true;
          write(status, '');
          expired.hidden = false;
        }
      }

      function tick() {
        var remaining = Math.floor((deadline - now()) / SECOND);

        if (remaining <= 0) {
          expire();
          return;
        }

        paint(remaining);
      }

      /* Coming back from a sleeping tab or a background window: repaint at once
         instead of showing a stale value until the next interval fires. */
      function onVisibility() {
        if (document.visibilityState === 'visible' && !finished) tick();
      }

      tick();

      if (!finished) {
        interval = window.setInterval(tick, SECOND);
        document.addEventListener('visibilitychange', onVisibility);

        teardown.push(function () {
          stop();
          document.removeEventListener('visibilitychange', onVisibility);
        });
      }

      instances.set(el, teardown);
    });
  }

  function destroy(root) {
    var scope = root || document;

    forEach(scope.querySelectorAll('[data-blv-countdown]'), function (el) {
      var teardown = instances.get(el);
      if (!teardown) return;

      teardown.forEach(function (fn) {
        fn();
      });
      instances.delete(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  window.addEventListener('resize', onResize, { passive: true });

  document.addEventListener('shopify:section:load', function (event) {
    measureHeaderOffset();
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });
})();
